import { CalendarToday } from '@mui/icons-material';
import { Stack, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router-dom';
import CreateVisitModal from '/@/components/field-list-v2/components/sidebar/components/create-visit-modal';
import { useNextVisitDashboard } from '/@/hooks/dashboards';
import { useGetUserNode } from '/@/hooks/nodes';
import { useIsValidatedConsultant, useIsValidatedRV } from '/@/hooks/session';
import { _ } from '/@/locale';
import { getSessionLanguage } from '/@/store/session/selectors';
import { mpTrackEvent } from '/@/store/tracking/mixpanel-events';
import DashboardCard from '../../../../../components/bayer/dashboard-card';
import NextVisitModal from '../next-visit-modal';

const NextVisitCard = ({ growerNodeId }: { growerNodeId: number }) => {
    const [openNextVisitModal, setOpenNextVisitModal] = useState(false);
    const [openCreateVisitModal, setOpenCreateVisitModal] = useState(false);
    const [searchParams, setSearchParams] = useSearchParams();
    const openVisitReportFromUrl = searchParams.get('openVisitReport') === 'true';
    const language = useSelector(getSessionLanguage);
    const hasProcessedUrlParam = useRef(false);

    // Auto-open modal when returning from field creation
    useEffect(() => {
        if (openVisitReportFromUrl && !hasProcessedUrlParam.current) {
            hasProcessedUrlParam.current = true;
            setOpenCreateVisitModal(true);
            // Clear the param to avoid re-opening on refresh
            const newParams = new URLSearchParams(searchParams);
            newParams.delete('openVisitReport');
            setSearchParams(newParams, { replace: true });
        }
    }, [openVisitReportFromUrl, searchParams, setSearchParams]);
    const nextVisitDashboardQuery = useNextVisitDashboard({
        id: growerNodeId,
    });
    const growerQuery = useGetUserNode(growerNodeId);
    const isConsultant = useIsValidatedConsultant();
    const isRV = useIsValidatedRV();

    const hasMessage = nextVisitDashboardQuery.data?.description !== undefined;

    return (
        <>
            <DashboardCard
                title={_('Próxima visita sugerida')}
                subtitle={hasMessage ? _(nextVisitDashboardQuery.data?.description) : undefined}
                number={
                    nextVisitDashboardQuery.data?.nextVisit ? (
                        <>
                            <CalendarToday sx={{ width: 24, height: 24, mr: 1 }} color='primary' />
                            {dayjs(nextVisitDashboardQuery.data.nextVisit)
                                .locale(language || 'en')
                                .format(language === 'en' ? 'MM/DD' : 'DD/MM')}
                        </>
                    ) : undefined
                }
                buttonProps={
                    isConsultant || isRV
                        ? {
                              children: _('Agendar visita'),
                              onClick: () => {
                                  setOpenNextVisitModal(true);
                              },
                          }
                        : {}
                }
                secondaryButtonProps={
                    isConsultant || isRV
                        ? {
                              children: _('Crear informe'),
                              onClick: () => {
                                  if (growerQuery.data) {
                                      mpTrackEvent('Visit Scheduling flow:Started', {
                                          grower_id: growerNodeId,
                                      });
                                  }
                                  setOpenCreateVisitModal(true);
                              },
                          }
                        : {}
                }
                isLoading={nextVisitDashboardQuery.isLoading}
            >
                {!hasMessage ? (
                    <Stack spacing={0.5}>
                        <Typography color='primary.mui.inactive' variant='subtitle2' fontWeight='bold'>
                            {_('No hay una visita sugerida por el momento.')}
                        </Typography>
                        {(isConsultant || isRV) && (
                            <Typography color='primary.mui.inactive' variant='body2'>
                                {_('Podés agendar una visita y crear el informe cuando lo consideres necesario.')}
                            </Typography>
                        )}
                    </Stack>
                ) : undefined}
            </DashboardCard>
            {openCreateVisitModal && (
                <CreateVisitModal
                    open={openCreateVisitModal}
                    setOpen={setOpenCreateVisitModal}
                    userNodeId={growerNodeId}
                />
            )}
            {growerQuery.data && (
                <NextVisitModal
                    open={openNextVisitModal}
                    onClose={() => setOpenNextVisitModal(false)}
                    growerName={growerQuery.data.name}
                    growerPhone={growerQuery.data.phone}
                    growerId={growerNodeId}
                />
            )}
        </>
    );
};

export default NextVisitCard;
